import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import { useAdminApiClient } from '../api/AdminApiClientContext'
import { queryKeys } from '../api/queryKeys'
import type {
  DaemonKind,
  DeathConfirmationState,
  DeliveryStatus,
  HealthResponse,
  HealthSnapshot,
} from '../api/types'
import { AlertsPanel } from '../components/AlertsPanel'
import { CodeChip } from '../components/CodeChip'
import { RelativeTime } from '../components/RelativeTime'

const DC_STATES: DeathConfirmationState[] = [
  'ACTIVE', 'ESCALATING', 'GRACE_PERIOD', 'FINAL_WARNING', 'DELIVERING',
]

const DELIVERY_STATUSES: DeliveryStatus[] = [
  'PENDING', 'SENDING', 'SENT', 'DELIVERED',
  'FAILED_TRANSIENT', 'FAILED_PERMANENT', 'READ', 'EXPIRED', 'REVOKED',
]

const DAEMON_KINDS: DaemonKind[] = [
  'CONFIRMATION', 'DELIVERY', 'RETENTION_CLEANUP',
]

const MAILGUN_TOTALS_OF_INTEREST = ['accepted', 'delivered', 'failed'] as const

interface QueueLink {
  to: string
  label: string
  count: number
}

function queueLinks(snapshot: HealthSnapshot): QueueLink[] {
  return [
    {
      to: '/death-confirmation',
      label: 'Death-confirmation queue',
      count: snapshot.operator_queue_badges.death_confirmation_attention,
    },
    {
      to: '/deliveries',
      label: 'Delivery problems',
      count: snapshot.operator_queue_badges.delivery_problems,
    },
  ]
}

export function HealthPage() {
  const client = useAdminApiClient()
  const query = useQuery({
    queryKey: queryKeys.health(),
    queryFn: () => client.get<HealthResponse>('/r/admin/health'),
    refetchInterval: 60_000,
  })

  if (query.isLoading) {
    return (
      <div className="space-y-6 p-6">
        <AlertsPanel />
        <div className="text-sm text-stone-500">Loading health snapshot…</div>
      </div>
    )
  }
  if (query.isError || !query.data?.success) {
    return (
      <div className="space-y-6 p-6">
        <AlertsPanel />
        <div
          className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm
                     text-red-800"
          role="alert"
        >
          <p className="font-semibold">Failed to load health snapshot.</p>
          <p className="mt-1 text-xs text-red-700">
            The backend returned an error. Try refreshing in a few seconds.
          </p>
          <button
            type="button"
            onClick={() => query.refetch()}
            className="mt-3 inline-flex rounded-md border border-red-300
                       bg-white px-3 py-1 text-xs font-medium text-red-700
                       hover:bg-red-50"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  const snapshot = query.data.data

  return (
    <div className="space-y-6 p-6">
      <header className="flex items-baseline justify-between">
        <h1 className="text-xl font-semibold text-stone-900">Health overview</h1>
        <span className="text-xs text-stone-500">
          Snapshot at <RelativeTime timestamp={snapshot.now} />
        </span>
      </header>

      <AlertsPanel />

      <section
        aria-labelledby="dc-state-counts"
        className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm"
      >
        <h2
          id="dc-state-counts"
          className="text-sm font-medium uppercase tracking-wide text-stone-600"
        >
          Death-confirmation states
        </h2>
        <dl className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-5">
          {DC_STATES.map((state) => (
            <div key={state} data-testid={`dc-state-${state}`}>
              <dt className="text-xs text-stone-500">{state}</dt>
              <dd className="text-2xl font-semibold text-stone-900">
                {snapshot.death_confirmation_state_counts[state] ?? 0}
              </dd>
            </div>
          ))}
        </dl>
      </section>

      <section
        aria-labelledby="delivery-counts"
        className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm"
      >
        <h2
          id="delivery-counts"
          className="text-sm font-medium uppercase tracking-wide text-stone-600"
        >
          Delivery statuses
        </h2>
        <dl className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {DELIVERY_STATUSES.map((status) => (
            <div key={status} data-testid={`delivery-status-${status}`}>
              <dt className="text-xs text-stone-500">{status}</dt>
              <dd className="text-2xl font-semibold text-stone-900">
                {snapshot.delivery_status_counts[status] ?? 0}
              </dd>
            </div>
          ))}
        </dl>
      </section>

      <section
        aria-labelledby="daemon-runs"
        className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm"
      >
        <h2
          id="daemon-runs"
          className="text-sm font-medium uppercase tracking-wide text-stone-600"
        >
          Last daemon run
        </h2>
        <ul className="mt-3 space-y-2 text-sm">
          {DAEMON_KINDS.map((kind) => {
            const run = snapshot.last_daemon_runs[kind]
            return (
              <li key={kind} className="flex flex-wrap items-center gap-2">
                <CodeChip>{kind}</CodeChip>
                {run ? (
                  <>
                    <span>
                      <RelativeTime timestamp={run.finished_time} />
                    </span>
                    <span
                      className={[
                        'rounded-full px-2 py-0.5 text-xs font-medium',
                        run.success
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-red-100 text-red-800',
                      ].join(' ')}
                    >
                      {run.success ? 'OK' : 'FAILED'}
                    </span>
                    <span className="text-xs text-stone-500">
                      {run.duration_ms} ms
                    </span>
                    {!run.success && run.error_message && (
                      <span className="text-xs text-red-700">
                        {run.error_message}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-xs text-stone-500">never run</span>
                )}
              </li>
            )
          })}
        </ul>
      </section>

      <section
        aria-labelledby="mailgun"
        className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm"
      >
        <h2
          id="mailgun"
          className="text-sm font-medium uppercase tracking-wide text-stone-600"
        >
          Mailgun
        </h2>
        <dl className="mt-3 grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
          {MAILGUN_TOTALS_OF_INTEREST.map((key) => (
            <div key={key} data-testid={`mailgun-${key}`}>
              <dt className="text-xs uppercase text-stone-500">{key}</dt>
              <dd className="text-2xl font-semibold text-stone-900">
                {snapshot.mailgun.totals[key] ?? 0}
              </dd>
            </div>
          ))}
          <div data-testid="mailgun-last-event">
            <dt className="text-xs uppercase text-stone-500">last event</dt>
            <dd className="text-sm text-stone-800">
              <RelativeTime
                timestamp={snapshot.mailgun.last_event_time}
                emptyLabel="never"
              />
            </dd>
          </div>
          <div data-testid="mailgun-signature-failures">
            <dt className="text-xs uppercase text-stone-500">
              last signature failure
            </dt>
            <dd className="text-sm text-stone-800">
              <RelativeTime
                timestamp={snapshot.mailgun.last_signature_failure_time}
                emptyLabel="none recorded"
              />
            </dd>
          </div>
        </dl>
      </section>

      <section
        aria-labelledby="queues"
        className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm"
      >
        <h2
          id="queues"
          className="text-sm font-medium uppercase tracking-wide text-stone-600"
        >
          Operator queues
        </h2>
        <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {queueLinks(snapshot).map((q) => (
            <li key={q.to}>
              <Link
                to={q.to}
                className="flex items-center justify-between rounded-md border
                           border-stone-200 px-3 py-2 text-sm hover:bg-accent/5"
                data-testid={`queue-link-${q.to}`}
              >
                <span className="font-medium text-stone-800">{q.label}</span>
                <span
                  className={[
                    'rounded-full px-2 py-0.5 text-xs font-semibold',
                    q.count > 0
                      ? 'bg-amber-100 text-amber-900'
                      : 'bg-stone-100 text-stone-600',
                  ].join(' ')}
                  data-testid={`queue-badge-${q.to}`}
                >
                  {q.count}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </div>
  )
}
